import React, { useState } from 'react';
import {
  Frame,
  TopBar,
  Navigation,
  Button,
  Text,
  Badge,
  InlineStack,
  Popover,
  ActionList,
  Icon,
  Avatar
} from '@shopify/polaris';
import {
  HomeIcon,
  SettingsIcon,
  ProfileIcon,
  ChevronDownIcon,
  ImportIcon,
  AutomationIcon,
  ChartLineIcon,
  ViewIcon
} from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ClientSelectionModal from './ClientSelectionModal';
import CreditAlert from './CreditAlert';

interface NavigationItem {
  label: string;
  url: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { selectedClient, clearSelectedClient } = useClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuActive, setUserMenuActive] = useState(false);
  const [clientSelectionModalOpen, setClientSelectionModalOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchClient = () => {
    setClientSelectionModalOpen(true);
  };

  // Define navigation items based on user role
  const getNavigationItems = (): NavigationItem[] => {
    if (!user) return [];

    if (user.role === 'support_manager') {
      return [
        {
          label: 'Dashboard',
          url: '/',
          icon: HomeIcon
        },
        {
          label: 'Usage Analytics',
          url: '/usage',
          icon: ViewIcon
        },
        {
          label: 'Billing',
          url: '/billing',
          icon: ChartLineIcon
        },
        {
          label: 'AI Prompt Management',
          url: '/ai-prompt-management',
          icon: AutomationIcon
        },
        {
          label: 'Settings',
          url: '/settings',
          icon: SettingsIcon
        },
        {
          label: 'Profile',
          url: '/profile',
          icon: ProfileIcon
        }
      ];
    }

    return [
      {
        label: 'Dashboard',
        url: '/',
        icon: HomeIcon
      },
      {
        label: 'Usage Analytics',
        url: '/usage',
        icon: ViewIcon
      },
      {
        label: 'Call Logs',
        url: '/call-logs',
        icon: ChartLineIcon
      },
      {
        label: 'Import Jobs',
        url: '/import-jobs',
        icon: ImportIcon
      },
      {
        label: 'Settings',
        url: '/settings',
        icon: SettingsIcon
      },
      {
        label: 'Profile',
        url: '/profile',
        icon: ProfileIcon
      }
    ];
  };

  const navigationItems = getNavigationItems();

  const userMenuActions = [
    {
      items: [
        {
          content: 'Profile',
          onAction: () => navigate('/profile')
        },
        {
          content: 'Logout',
          onAction: handleLogout
        }
      ]
    }
  ];

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={
        <TopBar.UserMenu
          actions={userMenuActions}
          name={user?.email || 'User'}
          detail={user?.role}
          initials={user?.email?.charAt(0) || 'U'}
          open={userMenuActive}
          onToggle={() => setUserMenuActive(!userMenuActive)}
        />
      }
    />
  );

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={navigationItems.map(item => ({
          label: item.label,
          icon: item.icon,
          selected: location.pathname === item.url,
          onClick: () => navigate(item.url)
        }))}
      />
    </Navigation>
  );

  // Hide navigation for support managers without selected client
  const shouldShowNavigation = !(user?.role === 'support_manager' && !selectedClient);

  return (
    <>
      <Frame
        topBar={topBarMarkup}
        navigation={shouldShowNavigation ? navigationMarkup : undefined}
      >
        {/* Client Context Header for Support Managers */}
        {user?.role === 'support_manager' && selectedClient && (
          <div style={{
            padding: '16px 24px',
            backgroundColor: '#f6f6f7',
            borderBottom: '1px solid #e1e3e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                Managing: {selectedClient.name}
              </Text>
              {selectedClient.has_active_plan && (
                <Badge tone="success">Active</Badge>
              )}
              {selectedClient.available_credits !== undefined && (
                <Badge tone="warning">{`${selectedClient.available_credits} Credits`}</Badge>
              )}
            </div>
            <Button
              variant="secondary"
              size="slim"
              onClick={handleSwitchClient}
            >
              Switch Client
            </Button>
          </div>
        )}
        
        <CreditAlert />
        
        {children}
      </Frame>
      
      <ClientSelectionModal
        open={clientSelectionModalOpen}
        onClose={() => setClientSelectionModalOpen(false)}
      />
    </>
  );
};

export default Layout;
